import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Download, Inbox, LogOut, Mail, Phone, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "socify-admin-token";

const TEMPLATES = [
  { id: "gracias", label: "Gracias", text: "Hola {nombre}, soy Abraham de Socify Marketing. Gracias por tu mensaje sobre {servicio}; estoy preparándote la propuesta y te escribo en breve." },
  { id: "presupuesto", label: "Presupuesto enviado", text: "Hola {nombre}, soy Abraham de Socify. Acabo de enviarte el presupuesto de {servicio}. Cualquier duda, me dices por aquí." },
  { id: "quedamos", label: "Quedamos", text: "Hola {nombre}, soy Abraham de Socify. ¿Te viene bien que hablemos esta semana para concretar tu proyecto de {servicio}? Dime qué día te va mejor." },
];

const waNumber = (phone) => {
  const digits = String(phone || "").replace(/[^0-9]/g, "");
  return digits.length === 9 ? `34${digits}` : digits;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-all focus:border-[#1D4ED8] focus:ring-4 focus:ring-[#1D4ED8]/10";

function Header() {
  return (
    <header className="border-b border-slate-200 bg-white/70 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
        <span className="font-display text-lg font-extrabold tracking-tight text-slate-900">
          SOCIFY<span className="text-[#1D4ED8]">.</span>
          <span className="ml-2 font-mono-sig text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">Panel</span>
        </span>
        <a
          data-testid="admin-back-link"
          href="/"
          className="group flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 transition-colors hover:bg-slate-900 hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
          Volver a la web
        </a>
      </div>
    </header>
  );
}

export default function AdminLeads() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState(null);
  const [payments, setPayments] = useState(null);
  const [tab, setTab] = useState("mensajes");

  const loadLeads = async (t = token) => {
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${t}` } });
      setLeads(data.leads);
      setError("");
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setLeads(null);
      } else {
        setError(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
  };

  useEffect(() => {
    if (token) {
      loadLeads();
      loadPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadPayments = async (t = token) => {
    try {
      const { data } = await axios.get(`${API}/admin/payments`, { headers: { Authorization: `Bearer ${t}` } });
      setPayments(data.payments);
    } catch {
      setPayments([]);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setLeads(null);
  };

  const deleteLead = async (id) => {
    if (!window.confirm("¿Eliminar este mensaje? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads((prev) => prev.filter((l) => l.id !== id));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleDone = async (lead) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/leads/${lead.id}`,
        { done: !lead.done },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, done: data.done } : l)));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const formatDate = (iso) =>
    new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const stats = leads
    ? {
        total: leads.length,
        pendientes: leads.filter((l) => !l.done).length,
        contestados: leads.filter((l) => l.done).length,
        topService:
          Object.entries(
            leads.reduce((acc, l) => {
              acc[l.service] = (acc[l.service] || 0) + 1;
              return acc;
            }, {})
          ).sort((a, b) => b[1] - a[1])[0]?.[0] || null,
      }
    : null;

  const weekly = (() => {
    const labels = ["D", "L", "M", "X", "J", "V", "S"];
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      days.push({ key: d.toDateString(), label: labels[d.getDay()], count: 0 });
    }
    (leads || []).forEach((l) => {
      const day = days.find((d) => d.key === new Date(l.created_at).toDateString());
      if (day) day.count += 1;
    });
    return days;
  })();

  const exportCSV = () => {
    if (!leads?.length) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [
      ["Nombre", "Email", "Teléfono", "Servicio", "Presupuesto", "Mensaje", "Fecha", "Estado"].map(esc).join(","),
      ...leads.map((l) =>
        [l.name, l.email, l.phone, l.service, l.budget, l.message, formatDate(l.created_at), l.done ? "Contestado" : "Pendiente"]
          .map(esc)
          .join(",")
      ),
    ];
    const blob = new Blob(["\ufeff" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contactos-socify.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <main className="mx-auto max-w-5xl px-6 py-14">
        {!token ? (
          <motion.form
            data-testid="admin-login-form"
            onSubmit={login}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-900/5 sm:p-10"
          >
            <p className="mb-2 font-mono-sig text-[10px] font-bold uppercase tracking-[0.3em] text-[#1D4ED8]">
              Acceso privado
            </p>
            <h1 className="mb-8 font-display text-2xl font-extrabold tracking-tight text-slate-900">
              Panel de mensajes
            </h1>
            <div className="space-y-4">
              <input
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className={inputCls}
              />
              <input
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Contraseña"
                className={inputCls}
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="mt-6 w-full rounded-full bg-slate-900 py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-60"
            >
              {loading ? "Entrando…" : "Entrar"}
            </button>
          </motion.form>
        ) : (
          <div>
            <div className="mb-10 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="mb-2 font-mono-sig text-[10px] font-bold uppercase tracking-[0.3em] text-[#1D4ED8]">
                  Mensajes recibidos
                </p>
                <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">
                  {leads ? `${leads.length} contacto${leads.length === 1 ? "" : "s"}` : "Cargando…"}
                </h1>
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="admin-export-csv-btn"
                  onClick={exportCSV}
                  className="flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2.5 text-xs font-bold text-slate-700 transition-colors hover:border-slate-900 hover:text-slate-900"
                >
                  <Download className="h-3.5 w-3.5" /> Exportar CSV
                </button>
                <button
                  data-testid="admin-refresh-btn"
                  onClick={() => loadLeads()}
                  className="flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2.5 text-xs font-bold text-slate-700 transition-colors hover:border-slate-900 hover:text-slate-900"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Actualizar
                </button>
                <button
                  data-testid="admin-logout-btn"
                  onClick={logout}
                  className="flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-red-600"
                >
                  <LogOut className="h-3.5 w-3.5" /> Salir
                </button>
              </div>
            </div>

            {stats && stats.total > 0 && (
              <div data-testid="admin-stats" className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div data-testid="admin-stat-total" className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="font-mono-sig text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Total</p>
                  <p className="mt-1 font-display text-3xl font-extrabold text-slate-900">{stats.total}</p>
                </div>
                <div data-testid="admin-stat-pendientes" className="rounded-2xl border border-[#FF9F00]/40 bg-[#FF9F00]/5 p-5">
                  <p className="font-mono-sig text-[10px] font-bold uppercase tracking-[0.2em] text-[#b46f00]">Pendientes</p>
                  <p className="mt-1 font-display text-3xl font-extrabold text-slate-900">{stats.pendientes}</p>
                </div>
                <div data-testid="admin-stat-contestados" className="rounded-2xl border border-green-200 bg-green-50 p-5">
                  <p className="font-mono-sig text-[10px] font-bold uppercase tracking-[0.2em] text-green-700">Contestados</p>
                  <p className="mt-1 font-display text-3xl font-extrabold text-slate-900">{stats.contestados}</p>
                </div>
                <div data-testid="admin-stat-top-service" className="rounded-2xl border border-[#1D4ED8]/30 bg-[#1D4ED8]/5 p-5">
                  <p className="font-mono-sig text-[10px] font-bold uppercase tracking-[0.2em] text-[#1D4ED8]">Más pedido</p>
                  <p className="mt-1 truncate font-display text-lg font-extrabold leading-8 text-slate-900">{stats.topService || "—"}</p>
                </div>
              </div>
            )}

            {leads && leads.length > 0 && (
              <div data-testid="admin-weekly-chart" className="mb-8 rounded-2xl border border-slate-200 bg-white p-6">
                <p className="mb-5 font-mono-sig text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                  Mensajes · últimos 7 días
                </p>
                <div className="flex h-32 items-end gap-2 sm:gap-3">
                  {weekly.map((d, i) => (
                    <div key={`${d.key}-${i}`} data-testid={`admin-chart-day-${i + 1}`} className="flex flex-1 flex-col items-center gap-2">
                      <span className="font-mono-sig text-[10px] font-bold text-slate-500">{d.count || ""}</span>
                      <div
                        className={`w-full rounded-t-lg transition-all duration-500 ${d.count ? "bg-[#1D4ED8]" : "bg-slate-100"}`}
                        style={{ height: `${Math.max(d.count * 28, 6)}px` }}
                      />
                      <span className="font-mono-sig text-[10px] uppercase tracking-widest text-slate-400">{d.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {error && (
              <p data-testid="admin-leads-error" className="mb-6 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
                {error}
              </p>
            )}

            {tab === "mensajes" && leads && leads.length === 0 && (
              <div data-testid="admin-leads-empty" className="rounded-3xl border border-dashed border-slate-300 bg-white p-14 text-center">
                <Inbox className="mx-auto mb-4 h-10 w-10 text-slate-300" />
                <p className="font-display text-xl font-bold text-slate-900">Aún no hay mensajes</p>
                <p className="mt-2 text-sm text-slate-500">Cuando alguien rellene el formulario de la web, aparecerá aquí.</p>
              </div>
            )}

            <div data-testid="admin-tabs" className="mb-8 flex gap-2.5">
              {[
                { id: "mensajes", label: "Mensajes" },
                { id: "pagos", label: "Pagos" },
              ].map((t) => (
                <button
                  key={t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`rounded-full border px-5 py-2.5 font-mono-sig text-[11px] font-bold uppercase tracking-[0.2em] transition-all ${
                    tab === t.id
                      ? "border-slate-900 bg-slate-900 text-[#00F0FF] shadow-lg"
                      : "border-slate-300 bg-white text-slate-500 hover:border-slate-900 hover:text-slate-900"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "mensajes" && (
            <div data-testid="admin-leads-list" className="space-y-4">
              {leads &&
                leads.map((lead, i) => (
                  <motion.article
                    key={lead.id}
                    data-testid={`admin-lead-item-${i + 1}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    className={`rounded-2xl border bg-white p-6 shadow-sm transition-opacity ${lead.done ? "border-green-200 opacity-60" : "border-slate-200"}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-lg font-bold text-slate-900">{lead.name}</p>
                        <p className="font-mono-sig text-[10px] uppercase tracking-[0.2em] text-slate-400">
                          {formatDate(lead.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-[#1D4ED8]/10 px-3 py-1 font-mono-sig text-[10px] font-bold uppercase tracking-widest text-[#1D4ED8]">
                          {lead.service}
                        </span>
                        <button
                          data-testid={`admin-lead-toggle-${i + 1}`}
                          onClick={() => toggleDone(lead)}
                          title={lead.done ? "Marcar como pendiente" : "Marcar como contestado"}
                          className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-mono-sig text-[10px] font-bold uppercase tracking-widest transition-colors ${
                            lead.done
                              ? "border-green-500 bg-green-50 text-green-700"
                              : "border-slate-300 text-slate-500 hover:border-green-500 hover:text-green-700"
                          }`}
                        >
                          <Check className="h-3.5 w-3.5" />
                          {lead.done ? "Contestado" : "Pendiente"}
                        </button>
                        <button
                          data-testid={`admin-lead-delete-${i + 1}`}
                          onClick={() => deleteLead(lead.id)}
                          title="Eliminar mensaje"
                          className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition-colors hover:border-red-500 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <p className="mt-4 rounded-xl bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-700">
                      {lead.message}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                      <a href={`mailto:${lead.email}`} className="flex items-center gap-2 font-medium text-[#1D4ED8] hover:underline">
                        <Mail className="h-4 w-4" /> {lead.email}
                      </a>
                      <a
                        href={`https://wa.me/${waNumber(lead.phone)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 font-medium text-[#25D366] hover:underline"
                      >
                        <Phone className="h-4 w-4" /> {lead.phone}
                      </a>
                      {lead.budget && (
                        <span className="font-mono-sig text-xs uppercase tracking-widest text-slate-500">
                          Presupuesto: {lead.budget}
                        </span>
                      )}
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                      <span className="font-mono-sig text-[10px] uppercase tracking-widest text-slate-400">
                        Respuesta rápida:
                      </span>
                      {TEMPLATES.map((t) => (
                        <a
                          key={t.id}
                          data-testid={`admin-lead-template-${t.id}-${i + 1}`}
                          href={`https://wa.me/${waNumber(lead.phone)}?text=${encodeURIComponent(
                            t.text.replace("{nombre}", lead.name).replace("{servicio}", lead.service)
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-full border border-[#25D366]/40 bg-[#25D366]/5 px-3 py-1.5 text-xs font-bold text-[#128C4A] transition-colors hover:bg-[#25D366] hover:text-white"
                        >
                          {t.label}
                        </a>
                      ))}
                    </div>
                  </motion.article>
                ))}
            </div>
            )}

            {tab === "pagos" && (
              <div data-testid="admin-payments-list" className="space-y-4">
                {payments === null ? (
                  <p className="text-sm text-slate-500">Cargando pagos…</p>
                ) : payments.length === 0 ? (
                  <div data-testid="admin-payments-empty" className="rounded-3xl border border-dashed border-slate-300 bg-white p-14 text-center">
                    <p className="font-display text-xl font-bold text-slate-900">Aún no hay pagos</p>
                    <p className="mt-2 text-sm text-slate-500">
                      Cuando un cliente pague con PayPal desde la web, aparecerá aquí con su importe y estado.
                    </p>
                  </div>
                ) : (
                  payments.map((p, i) => (
                    <motion.article
                      key={p.id}
                      data-testid={`admin-payment-item-${i + 1}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: i * 0.05 }}
                      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-display text-2xl font-extrabold text-slate-900">
                            {p.amount} {p.currency === "EUR" ? "€" : p.currency}
                          </p>
                          <p className="font-mono-sig text-[10px] uppercase tracking-[0.2em] text-slate-400">
                            {formatDate(p.created_at)}
                          </p>
                        </div>
                        <span
                          className={`rounded-full px-3 py-1 font-mono-sig text-[10px] font-bold uppercase tracking-widest ${
                            p.status === "COMPLETED" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {p.status === "COMPLETED" ? "Completado" : p.status}
                        </span>
                      </div>
                      <p className="mt-3 text-sm text-slate-600">{p.description}</p>
                      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                        {p.payer_email && (
                          <a href={`mailto:${p.payer_email}`} className="flex items-center gap-2 font-medium text-[#1D4ED8] hover:underline">
                            <Mail className="h-4 w-4" /> {p.payer_email}
                          </a>
                        )}
                        <span className="font-mono-sig text-xs uppercase tracking-widest text-slate-400">
                          Pedido: {p.order_id}
                        </span>
                      </div>
                    </motion.article>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
